import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ArrowLeft, Inbox, Star, Clock, Send, FileText, Trash2, AlertCircle, Tag, Settings, HelpCircle, Zap, BarChart3, Shield, Layers, ListPlus, PenSquare } from 'lucide-react';
import { normalizeImportance } from '../utils/formatters';
import EmailSelector from './EmailSelector';
import ProcessingView from './ProcessingView';
import ResultsView from './ResultsView';
import TrashAnalyzedView from './TrashAnalyzedView';
import MessageCollectionModal from './MessageCollectionModal';
import EmailViewerModal from './EmailViewerModal';
import ComposeModal from './ComposeModal';
import SettingsModal from './SettingsModal';
import { logout as apiLogout, fetchEmails, fetchSnoozeLabelId, fetchAccountSummary } from '../utils/api';
import {
  loadSessionsNormalized,
  persistSessions,
  createSession,
  collectAnalyzedEmailIds,
  normalizeSessionsRemoveDuplicates,
} from '../utils/analysisStorage';
import { loadCollection, persistCollection, collectionItemKey } from '../utils/collectionStorage';
import { loadTrash, persistTrash, pruneTrashByAge } from '../utils/trashStorage';
import { loadSettings, applyThemeToDocument } from '../utils/settingsStorage';
import { notify, confirmToast } from '../utils/notify';

function playAnalysisChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.04, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + 0.2);
  } catch {
    /* ignore */
  }
}

export default function Dashboard({ userEmail, onLogout }) {
  const initialSessions = loadSessionsNormalized();
  const [sessions, setSessions] = useState(initialSessions);
  const [activeSessionId, setActiveSessionId] = useState(() =>
    initialSessions.length ? initialSessions[initialSessions.length - 1].id : null
  );
  const [view, setView] = useState('select');
  const [processingIds, setProcessingIds] = useState([]);
  const [allResults, setAllResults] = useState(() => {
    if (!initialSessions.length) return [];
    return initialSessions[initialSessions.length - 1].results || [];
  });
  const [wasCancelled, setWasCancelled] = useState(false);
  const [totalRequested, setTotalRequested] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarFilter, setSidebarFilter] = useState('all');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('general');
  const [serverAccount, setServerAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [folderEmails, setFolderEmails] = useState([]);
  const [loadingFolder, setLoadingFolder] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [snoozeLabelId, setSnoozeLabelId] = useState(null);
  const [messageCollection, setMessageCollection] = useState(() => loadCollection());
  const [showPickMessagesModal, setShowPickMessagesModal] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeInitial, setComposeInitial] = useState({});
  const [viewerMessageId, setViewerMessageId] = useState(null);
  const [viewerFolder, setViewerFolder] = useState('inbox');
  const [appSettings, setAppSettings] = useState(() => loadSettings());
  const [trashItems, setTrashItems] = useState(() => {
    const s = loadSettings();
    let t = loadTrash();
    if (s.trashAutocleanEnabled) {
      const pruned = pruneTrashByAge(t, s.trashAutocleanDays);
      if (pruned.length !== t.length) persistTrash(pruned);
      t = pruned;
    }
    return t;
  });

  const loadAccount = useCallback(async () => {
    try {
      const d = await fetchAccountSummary();
      setServerAccount(d.account);
    } catch (e) {
      console.warn('[Dashboard] account summary:', e);
    }
  }, []);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = await fetchSnoozeLabelId();
        if (!cancelled) setSnoozeLabelId(id);
      } catch (e) {
        console.error('[Dashboard] Could not load snooze label id:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const analyzedEmailIds = useMemo(() => {
    const ids = collectAnalyzedEmailIds(sessions);
    for (const t of trashItems) {
      const eid = t?.result?.email?.id;
      if (eid) ids.add(eid);
    }
    return ids;
  }, [sessions, trashItems]);

  useEffect(() => {
    setMessageCollection((prev) => {
      const next = prev.filter((item) => {
        const sess = sessions.find((s) => s.id === item.sessionId);
        if (!sess) return false;
        return sess.results.some((r) => r.email?.id === item.result.email.id);
      });
      if (next.length === prev.length) return prev;
      persistCollection(next);
      return next;
    });
  }, [sessions]);

  useEffect(() => {
    if (!appSettings.trashAutocleanEnabled) return;
    setTrashItems((prev) => {
      const next = pruneTrashByAge(prev, appSettings.trashAutocleanDays);
      if (next.length !== prev.length) persistTrash(next);
      return next;
    });
  }, [appSettings.trashAutocleanEnabled, appSettings.trashAutocleanDays]);

  useEffect(() => {
    const id = setInterval(() => {
      const s = loadSettings();
      if (!s.trashAutocleanEnabled) return;
      setTrashItems((prev) => {
        const next = pruneTrashByAge(prev, s.trashAutocleanDays);
        if (next.length !== prev.length) persistTrash(next);
        return next;
      });
    }, 24 * 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const patchEmailInCollection = useCallback((messageId, partialEmail) => {
    setMessageCollection((prev) => {
      const next = prev.map((item) =>
        item.result.email.id === messageId
          ? { ...item, result: { ...item.result, email: { ...item.result.email, ...partialEmail } } }
          : item
      );
      persistCollection(next);
      return next;
    });
  }, []);

  const handleAddToCollection = (items) => {
    setMessageCollection((prev) => {
      const seen = new Set(prev.map((p) => collectionItemKey(p.sessionId, p.result.email.id)));
      const next = [...prev];
      for (const it of items) {
        const k = collectionItemKey(it.sessionId, it.result.email.id);
        if (!seen.has(k)) {
          seen.add(k);
          next.push(it);
        }
      }
      persistCollection(next);
      return next;
    });
    setSidebarFilter('all');
    setView('collection');
  };

  const clearMessageCollection = () => {
    setMessageCollection([]);
    persistCollection([]);
  };

  const patchEmailInActiveSession = (messageId, partialEmail) => {
    setAllResults((prev) =>
      prev.map((r) => (r.email.id === messageId ? { ...r, email: { ...r.email, ...partialEmail } } : r))
    );
    setSessions((prev) => {
      const next = prev.map((s) =>
        s.id === activeSessionId
          ? {
              ...s,
              results: s.results.map((r) =>
                r.email.id === messageId ? { ...r, email: { ...r.email, ...partialEmail } } : r
              ),
            }
          : s
      );
      persistSessions(next);
      return next;
    });
  };

  const handleAnalyze = (ids) => {
    if (!ids || ids.length === 0) return;
    const fresh = ids.filter((id) => !analyzedEmailIds.has(id));
    if (fresh.length === 0) {
      notify.info('All of those messages are already in your saved analyses.');
      return;
    }
    if (fresh.length < ids.length) {
      const skipped = ids.length - fresh.length;
      notify.info(
        `Skipped ${skipped} already-analyzed message${skipped === 1 ? '' : 's'}.`,
        { description: `Analyzing ${fresh.length} new message${fresh.length === 1 ? '' : 's'}.` }
      );
    }
    setProcessingIds(fresh);
    setTotalRequested(fresh.length);
    setWasCancelled(false);
    setView('processing');
  };

  const handleComplete = (res) => {
    const newSession = createSession(res);
    setSessions((prev) => {
      const next = normalizeSessionsRemoveDuplicates([...prev, newSession]);
      persistSessions(next);
      return next;
    });
    setActiveSessionId(newSession.id);
    setAllResults(res);
    setWasCancelled(false);
    setSidebarFilter('all');
    setView('results');
    loadAccount();
    if (loadSettings().soundEffects) playAnalysisChime();
  };

  const handleCancel = (partialResults) => {
    const newSession = createSession(partialResults);
    newSession.title = `Analysis (stopped) · ${new Date().toLocaleString()}`;
    setSessions((prev) => {
      const next = normalizeSessionsRemoveDuplicates([...prev, newSession]);
      persistSessions(next);
      return next;
    });
    setActiveSessionId(newSession.id);
    setAllResults(partialResults);
    setWasCancelled(true);
    setSidebarFilter('all');
    setView('results');
    loadAccount();
  };

  const handleSessionChange = (sessionId) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return;
    setActiveSessionId(sessionId);
    setAllResults(session.results || []);
    setSidebarFilter('all');
    setView('results');
  };

  const handleLogout = async () => {
    await apiLogout();
    onLogout();
  };

  const handleSettingsChange = useCallback((next) => {
    setAppSettings(next);
    applyThemeToDocument(next);
  }, []);

  const handleNewAnalysis = () => {
    setView('select');
    setProcessingIds([]);
    if (appSettings.autoRefresh) {
      setFolderEmails([]);
      setCurrentFolder('inbox');
      loadAccount();
    }
  };

  const openFolderInstant = useCallback(
    async (folder, opts = {}) => {
      setCurrentFolder(folder);
      setFolderEmails([]);
      setView('folder-view');
      setLoadingFolder(true);
      try {
        const emails = await fetchEmails(50, folder);
        setFolderEmails(emails);
        if (opts.onLoaded) opts.onLoaded(emails);
      } catch (e) {
        console.error(`Failed to fetch ${folder} emails:`, e);
      } finally {
        setLoadingFolder(false);
      }
    },
    []
  );

  const handleViewStarred = () => openFolderInstant('starred');
  const handleViewSnoozed = () => openFolderInstant('snoozed');
  const handleViewSent = () => openFolderInstant('sent');
  const handleViewDrafts = () =>
    openFolderInstant('drafts', { onLoaded: (emails) => setDraftCount(emails.length) });

  const handleViewTrash = () => {
    setView('trash-analyzed');
  };

  const handleDismissAnalyzed = useCallback(
    ({ sessionId, sessionTitle, emailId, result }) => {
      const key = collectionItemKey(sessionId, emailId);
      const { _sessionLabel, _resultKey, _sessionId, ...rest } = result;
      const entry = {
        key,
        sessionId,
        sessionTitle,
        result: rest,
        trashedAt: new Date().toISOString(),
      };
      setTrashItems((prev) => {
        const next = [entry, ...prev.filter((e) => e.key !== key)];
        persistTrash(next);
        return next;
      });
      setSessions((prev) => {
        const next = prev.map((s) =>
          s.id === sessionId ? { ...s, results: s.results.filter((r) => r.email.id !== emailId) } : s
        );
        persistSessions(next);
        return next;
      });
      setMessageCollection((prev) => {
        const next = prev.filter((item) => collectionItemKey(item.sessionId, item.result.email.id) !== key);
        persistCollection(next);
        return next;
      });
      setAllResults((prev) => {
        if (activeSessionId !== sessionId) return prev;
        return prev.filter((r) => r.email.id !== emailId);
      });
    },
    [activeSessionId]
  );

  const handleRestoreTrashEntry = useCallback(
    (entry) => {
      if (!sessions.some((s) => s.id === entry.sessionId)) {
        notify.warning('That analysis run no longer exists.', {
          description: 'Start a new analysis or remove this item from trash.',
        });
        return;
      }
      setTrashItems((prev) => {
        const next = prev.filter((e) => e.key !== entry.key);
        persistTrash(next);
        return next;
      });
      setSessions((prev) => {
        const next = prev.map((s) =>
          s.id === entry.sessionId ? { ...s, results: [...s.results, entry.result] } : s
        );
        persistSessions(next);
        return next;
      });
      if (activeSessionId === entry.sessionId) {
        setAllResults((prev) => [...prev, entry.result]);
      }
    },
    [sessions, activeSessionId]
  );

  const handleDeleteTrashEntryForever = useCallback((entry) => {
    setTrashItems((prev) => {
      const next = prev.filter((e) => e.key !== entry.key);
      persistTrash(next);
      return next;
    });
  }, []);

  const handleEmptyAnalyzedTrash = useCallback(() => {
    setTrashItems([]);
    persistTrash([]);
  }, []);

  const handleTrashAnalyzedBack = useCallback(() => {
    if (allResults.length > 0) {
      setView('results');
      return;
    }
    if (messageCollection.length > 0) {
      setView('collection');
      return;
    }
    setView('select');
  }, [allResults.length, messageCollection.length]);

  const openSettings = (tab = 'general') => {
    setSettingsInitialTab(tab);
    setShowSettingsModal(true);
  };

  const handleSidebarNav = (filter) => {
    if (view === 'trash-analyzed') {
      setSidebarFilter(filter);
      if (allResults.length > 0) setView('results');
      else if (messageCollection.length > 0) setView('collection');
      return;
    }
    if (view === 'collection') {
      setSidebarFilter(filter);
      return;
    }
    if (allResults.length === 0 && filter !== 'all') return;
    setSidebarFilter(filter);
    if (allResults.length > 0) setView('results');
  };

  const collectionAsResults = useMemo(
    () =>
      messageCollection.map((c) => ({
        ...c.result,
        _sessionId: c.sessionId,
        _sessionLabel: c.sessionTitle,
        _resultKey: collectionItemKey(c.sessionId, c.result.email.id),
      })),
    [messageCollection]
  );

  const pickModalExistingKeys = useMemo(
    () => messageCollection.map((c) => collectionItemKey(c.sessionId, c.result.email.id)),
    [messageCollection]
  );

  const sensitivityHiddenTiers = useMemo(() => {
    if (appSettings.analysisSensitivity === 'high') return new Set(['low', 'routine']);
    if (appSettings.analysisSensitivity === 'medium') return new Set(['low']);
    return new Set();
  }, [appSettings.analysisSensitivity]);

  const filteredCollection = useMemo(() => {
    let results = collectionAsResults;
    if (sidebarFilter === 'all' && sensitivityHiddenTiers.size > 0) {
      results = results.filter((r) => !sensitivityHiddenTiers.has(normalizeImportance(r.importance)));
    }
    if (sidebarFilter === 'all') {
    } else if (sidebarFilter === 'starred') {
      results = results.filter((r) => {
        const t = normalizeImportance(r.importance);
        return t === 'critical' || t === 'important';
      });
    } else if (sidebarFilter === 'critical') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'critical');
    } else if (sidebarFilter === 'important') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'important');
    } else if (sidebarFilter === 'routine') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'routine');
    } else if (sidebarFilter === 'low') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'low');
    } else if (sidebarFilter === 'unread') {
      results = results.filter((r) => r.email?.is_unread);
    } else if (sidebarFilter === 'snoozed') {
      results = results.filter((r) => snoozeLabelId && r.email?.labels?.includes(snoozeLabelId));
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(
        (r) =>
          r.email?.subject?.toLowerCase().includes(query) ||
          r.email?.sender?.toLowerCase().includes(query) ||
          r.email?.snippet?.toLowerCase().includes(query) ||
          r.summary?.toLowerCase().includes(query) ||
          (r._sessionLabel && r._sessionLabel.toLowerCase().includes(query))
      );
    }
    return results;
  }, [collectionAsResults, sidebarFilter, searchQuery, snoozeLabelId, sensitivityHiddenTiers]);

  const filteredResults = useMemo(() => {
    let results = allResults;
    if (sidebarFilter === 'all' && sensitivityHiddenTiers.size > 0) {
      results = results.filter((r) => !sensitivityHiddenTiers.has(normalizeImportance(r.importance)));
    }

    if (sidebarFilter === 'all') {
    } else if (sidebarFilter === 'starred') {
      results = results.filter((r) => {
        const t = normalizeImportance(r.importance);
        return t === 'critical' || t === 'important';
      });
    } else if (sidebarFilter === 'critical') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'critical');
    } else if (sidebarFilter === 'important') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'important');
    } else if (sidebarFilter === 'routine') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'routine');
    } else if (sidebarFilter === 'low') {
      results = results.filter((r) => normalizeImportance(r.importance) === 'low');
    } else if (sidebarFilter === 'unread') {
      results = results.filter(r => r.email?.is_unread);
    } else if (sidebarFilter === 'snoozed') {
      results = results.filter(
        (r) => snoozeLabelId && r.email?.labels?.includes(snoozeLabelId)
      );
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(r => 
        r.email?.subject?.toLowerCase().includes(query) ||
        r.email?.sender?.toLowerCase().includes(query) ||
        r.email?.snippet?.toLowerCase().includes(query) ||
        r.summary?.toLowerCase().includes(query)
      );
    }
    
    return results;
  }, [allResults, sidebarFilter, searchQuery, snoozeLabelId, sensitivityHiddenTiers]);

  // Count badges (follow current context: combined list vs active analysis run)
  const counts = useMemo(() => {
    const c = { critical: 0, important: 0, routine: 0, low: 0, unread: 0, starred: 0, snoozed: 0 };
    const source = view === 'collection' ? collectionAsResults : allResults;
    source.forEach((r) => {
      const t = normalizeImportance(r.importance);
      c[t] = (c[t] || 0) + 1;
      if (r.email?.is_unread) c.unread++;
      if (t === 'critical' || t === 'important') c.starred++;
      if (snoozeLabelId && r.email?.labels?.includes(snoozeLabelId)) c.snoozed++;
    });
    return c;
  }, [view, collectionAsResults, allResults, snoozeLabelId]);

  const hasResults = (view === 'collection' ? collectionAsResults.length : allResults.length) > 0;

  const sidebarItems = [
    { icon: Inbox, label: 'Inbox', active: view === 'select' || (view === 'folder-view' && currentFolder === 'inbox'), onClick: handleNewAnalysis, badge: null },
    { icon: Star, label: 'Starred', active: view === 'folder-view' && currentFolder === 'starred', onClick: handleViewStarred, badge: null },
    { icon: Clock, label: 'Snoozed', active: view === 'folder-view' && currentFolder === 'snoozed', onClick: handleViewSnoozed, badge: null },
    { icon: Send, label: 'Sent', active: view === 'folder-view' && currentFolder === 'sent', onClick: handleViewSent, badge: null },
    { icon: FileText, label: 'Drafts', active: view === 'folder-view' && currentFolder === 'drafts', onClick: handleViewDrafts, badge: draftCount > 0 ? draftCount : null },
    {
      icon: Trash2,
      label: 'Trash',
      active: view === 'trash-analyzed',
      onClick: handleViewTrash,
      badge: trashItems.length > 0 ? trashItems.length : null,
    },
  ];

  const sidebarLabels = [
    { icon: AlertCircle, label: 'Critical', color: '#ff2d55', filter: 'critical', badge: hasResults && counts.critical > 0 ? counts.critical : null },
    { icon: Zap, label: 'Important', color: '#ff9f0a', filter: 'important', badge: hasResults && counts.important > 0 ? counts.important : null },
    { icon: Tag, label: 'Routine', color: '#00f0ff', filter: 'routine', badge: hasResults && counts.routine > 0 ? counts.routine : null },
    { icon: Clock, label: 'Snoozed', color: '#5ac8fa', filter: 'snoozed', badge: hasResults && counts.snoozed > 0 ? counts.snoozed : null },
    { icon: BarChart3, label: 'Analytics', color: '#30d158', filter: 'all', badge: null },
  ];

  return (
    <div className="dashboard">
      <div className="bg-mesh" />

      <header className="topbar">
        <div className="topbar-left">
          <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <span className="hamburger" />
          </button>
          <div className="topbar-brand">
            <Zap size={20} className="brand-icon" />
            InboxPulse
          </div>
        </div>
        <div className="topbar-search">
          <input 
            type="text" 
            placeholder="Search in emails..." 
            className="topbar-search-input" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="topbar-user">
          <div className="status-dot" />
          <span className="topbar-email">{userEmail}</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setComposeInitial({});
              setShowCompose(true);
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <PenSquare size={16} /> Compose
          </button>
          <button className="btn btn-sm" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <div className="dashboard-body">
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-section">
            <button className="sidebar-compose" onClick={handleNewAnalysis}>
              <Zap size={18} />
              <span>New Analysis</span>
            </button>
          </div>

          <div className="sidebar-section-title">My messages</div>
          <nav className="sidebar-nav">
            <button
              type="button"
              className={`sidebar-item ${view === 'collection' ? 'active' : ''}`}
              onClick={() => {
                setSidebarFilter('all');
                setView('collection');
              }}
              disabled={messageCollection.length === 0}
            >
              <Layers size={18} />
              <span className="sidebar-label">Combined ({messageCollection.length})</span>
            </button>
            <button type="button" className="sidebar-item" onClick={() => setShowPickMessagesModal(true)}>
              <ListPlus size={18} />
              <span className="sidebar-label">Pick messages</span>
            </button>
          </nav>

          <div className="sidebar-divider" />

          <nav className="sidebar-nav">
            {sidebarItems.map((item) => (
              <button
                key={item.label}
                className={`sidebar-item ${item.active ? 'active' : ''}`}
                onClick={item.onClick}
              >
                <item.icon size={18} />
                <span className="sidebar-label">{item.label}</span>
                {item.badge != null && <span className="sidebar-badge">{item.badge}</span>}
              </button>
            ))}
          </nav>

          <div className="sidebar-divider" />
          <div className="sidebar-section-title">Labels</div>
          <nav className="sidebar-nav">
            {sidebarLabels.map((item) => (
              <button
                key={item.label}
                className={`sidebar-item ${sidebarFilter === item.filter ? 'active' : ''}`}
                onClick={() => handleSidebarNav(item.filter)}
              >
                <item.icon size={16} style={{ color: item.color }} />
                <span className="sidebar-label">{item.label}</span>
                {item.badge != null && <span className="sidebar-badge">{item.badge}</span>}
              </button>
            ))}
          </nav>

          <div className="sidebar-divider" />
          <nav className="sidebar-nav sidebar-bottom-nav">
            <button type="button" className="sidebar-item" onClick={() => openSettings('privacy')}>
              <Shield size={16} />
              <span className="sidebar-label">Privacy</span>
            </button>
            <button type="button" className="sidebar-item" onClick={() => openSettings('general')}>
              <Settings size={16} />
              <span className="sidebar-label">Settings</span>
            </button>
            <button type="button" className="sidebar-item" onClick={() => openSettings('help')}>
              <HelpCircle size={16} />
              <span className="sidebar-label">Help</span>
            </button>
          </nav>
        </aside>

        <main className={`dashboard-content ${appSettings.compactResults ? 'compact-results' : ''}`}>
          {view === 'collection' && (
            <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <button type="button" className="btn" onClick={handleNewAnalysis} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ArrowLeft size={16} /> Back to inbox
              </button>
              <button type="button" className="btn" onClick={() => setShowPickMessagesModal(true)}>
                Add messages…
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={clearMessageCollection}
                disabled={messageCollection.length === 0}
              >
                Clear list
              </button>
              <span style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                {messageCollection.length} message{messageCollection.length !== 1 ? 's' : ''} from your runs
              </span>
            </div>
          )}
          {view === 'results' && (
            <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <button type="button" className="btn" onClick={handleNewAnalysis} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ArrowLeft size={16} /> New Analysis
              </button>
              {sessions.length > 0 && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                  Saved run
                  <select
                    className="btn btn-sm"
                    value={activeSessionId || ''}
                    onChange={(e) => handleSessionChange(e.target.value)}
                    style={{ background: 'var(--surface-2)', color: 'var(--text)', minWidth: 220 }}
                  >
                    {sessions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title} ({s.results?.length ?? 0} emails)
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          {view === 'folder-view' && (
            <div style={{ marginBottom: 16 }}>
              <button className="btn" onClick={handleNewAnalysis} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ArrowLeft size={16} /> Back to Inbox
              </button>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginTop: 16, marginBottom: 8 }}>
                {currentFolder.charAt(0).toUpperCase() + currentFolder.slice(1)}
              </h2>
              <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                {folderEmails.length} emails found
              </p>
            </div>
          )}
          {view === 'select' && (
            <EmailSelector
              onAnalyze={handleAnalyze}
              analyzedEmailIds={analyzedEmailIds}
              account={serverAccount}
              onRefreshAccount={loadAccount}
              showTips={appSettings.showAnalysisTips}
              maxBatchCap={appSettings.maxEmailsPerBatch}
              sessions={sessions}
              userEmail={userEmail}
              onOpenSession={handleSessionChange}
            />
          )}
          {view === 'processing' && (
            <ProcessingView
              emailIds={processingIds}
              onComplete={handleComplete}
              onCancel={handleCancel}
              snoozeLabelId={snoozeLabelId}
            />
          )}
          {view === 'trash-analyzed' && (
            <TrashAnalyzedView
              items={trashItems}
              onBack={handleTrashAnalyzedBack}
              onRestore={handleRestoreTrashEntry}
              onDeleteForever={handleDeleteTrashEntryForever}
              onEmptyTrash={handleEmptyAnalyzedTrash}
            />
          )}
          {view === 'collection' && (
            <ResultsView
              results={filteredCollection}
              allResults={collectionAsResults}
              wasCancelled={false}
              totalRequested={messageCollection.length}
              filterLabel={sidebarFilter}
              snoozeLabelId={snoozeLabelId}
              onPatchEmail={patchEmailInCollection}
              sessionMeta={null}
              onDismiss={handleDismissAnalyzed}
            />
          )}
          {view === 'results' && (
            <ResultsView
              results={filteredResults}
              allResults={allResults}
              wasCancelled={wasCancelled}
              totalRequested={totalRequested}
              filterLabel={sidebarFilter}
              snoozeLabelId={snoozeLabelId}
              onPatchEmail={patchEmailInActiveSession}
              sessionMeta={
                activeSessionId
                  ? {
                      id: activeSessionId,
                      title: sessions.find((s) => s.id === activeSessionId)?.title || '',
                    }
                  : null
              }
              onDismiss={handleDismissAnalyzed}
            />
          )}
          {view === 'folder-view' && loadingFolder && (
            <div className="loading-center">
              <div className="loading-spinner" />
              <span>Loading {currentFolder} emails...</span>
            </div>
          )}
          {view === 'folder-view' && !loadingFolder && folderEmails.length === 0 && (
            <div className="loading-center">
              <span>No emails found in {currentFolder}</span>
            </div>
          )}
          {view === 'folder-view' && !loadingFolder && folderEmails.length > 0 && (
            <div className="email-grid">
              {folderEmails.map((email, idx) => (
                <div
                  key={email.id}
                  role="button"
                  tabIndex={0}
                  className="email-card glass"
                  style={{ animationDelay: `${idx * 0.05}s`, cursor: 'pointer' }}
                  onClick={() => {
                    setViewerMessageId(email.id);
                    setViewerFolder(currentFolder);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setViewerMessageId(email.id);
                      setViewerFolder(currentFolder);
                    }
                  }}
                >
                  <div className="email-card-header">
                    <div className="email-sender-row">
                      <div className="sender-avatar" style={{ background: 'var(--surface-3)', color: 'var(--text)' }}>
                        {email.sender?.charAt(0) || '?'}
                      </div>
                      <div className="email-sender-info">
                        <div className="email-sender-name">{email.sender || 'Unknown'}</div>
                        <div className="email-time">{email.date || ''}</div>
                      </div>
                    </div>
                  </div>
                  <div className="email-subject">{email.subject || 'No subject'}</div>
                  {(currentFolder === 'sent' || currentFolder === 'drafts') && email.to_preview && (
                    <div className="email-summary" style={{ color: 'var(--cyan)', fontSize: '0.8rem' }}>
                      To: {email.to_preview}
                    </div>
                  )}
                  <div className="email-summary" style={{ color: 'var(--text-dim)' }}>
                    {email.snippet || 'No preview available'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: 8 }}>Click to open · attachments in viewer</div>
                </div>
              ))}
            </div>
          )}
          <SettingsModal
            open={showSettingsModal}
            onClose={() => {
              setShowSettingsModal(false);
              const next = loadSettings();
              setAppSettings(next);
              applyThemeToDocument(next);
            }}
            onSettingsChange={handleSettingsChange}
            initialTab={settingsInitialTab}
            userEmail={userEmail}
            account={serverAccount}
            onAccountRefresh={loadAccount}
          />
          {showPickMessagesModal && (
            <MessageCollectionModal
              isOpen={showPickMessagesModal}
              sessions={sessions}
              existingKeys={pickModalExistingKeys}
              onClose={() => setShowPickMessagesModal(false)}
              onAddToCollection={handleAddToCollection}
            />
          )}
          {showCompose && (
            <ComposeModal
              open={showCompose}
              onClose={() => setShowCompose(false)}
              initial={composeInitial}
              onSent={() => {
                if (currentFolder === 'drafts') handleViewDrafts();
              }}
            />
          )}
          {viewerMessageId && (
            <EmailViewerModal
              messageId={viewerMessageId}
              folder={viewerFolder}
              onClose={() => setViewerMessageId(null)}
              onOpenCompose={(init) => {
                setComposeInitial(init);
                setShowCompose(true);
              }}
            />
          )}
        </main>
      </div>
    </div>
  );
}
